import { makeStyles, TableCell, TableRow } from '@material-ui/core'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { CostType } from '../../../generated/graphql'
import { theme } from '../../../theme'
import { formatPrice } from '../../../util/formatters'
import { NO_MATCHING_RESOURCE_SYMBOL } from '../../common/constants'
import { SelectedVersion } from '../../layout/VersionsSelector'
import { CostRow, FixedCostRow, isFixedCostRow } from './cost-table-types'
import { useCostTables } from './use-cost-tables'

type Props = {
  costRow: CostRow
  handleCostClick?: (
    versionId: string,
    costRow: FixedCostRow,
    groupedCostName: string,
  ) => void
  type: CostType | 'energy'
  versions: SelectedVersion[]
  title: string
}

const useStyles = makeStyles({
  editableCell: {
    cursor: 'pointer',
  },
  root: {
    paddingLeft: theme.spacing(5),
  },
})

export function CostsTableValueRow({
  costRow,
  handleCostClick,
  type,
  versions,
  title,
}: Props): React.ReactElement {
  const { t } = useTranslation()
  const classes = useStyles()
  const { checkIfProviderExistsForVersion } = useCostTables()

  const getCostRowTitle = (row: CostRow) => {
    if (isFixedCostRow(row)) {
      return row.providerName || row.description
    }
    return t(row.translationKey)
  }

  const getRowText = (row: CostRow) => {
    const namePostfix =
      isFixedCostRow(row) && row.providerName !== '' && row.description
        ? ` (${row.description})`
        : ''
    const rowTitle = getCostRowTitle(row)
    const text = rowTitle
      ? `${rowTitle}${namePostfix}`
      : `${isFixedCostRow(row) ? row.description : ''}`

    return text || t('costs.table.missingDescription')
  }
  const getCellSum = (
    versionId: string,
    groupedCostName: string,
    row: CostRow,
  ): string => {
    const noValueSign = type === 'energy' ? NO_MATCHING_RESOURCE_SYMBOL : '0'

    const isProviderInVersion = checkIfProviderExistsForVersion(
      groupedCostName,
      getCostRowTitle(row),
      versionId,
    )
    if (type !== 'energy' && !isProviderInVersion) {
      return NO_MATCHING_RESOURCE_SYMBOL
    }

    const cost = row.costs.find(
      (costRowCost) => costRowCost.versionId === versionId,
    )

    const sum = cost?.value

    const multiplierPostFix =
      cost?.multiplier && cost.multiplier > 1 ? ` (x${cost.multiplier})` : ''

    return sum && Math.round(sum) !== 0
      ? `${formatPrice(sum, { omitUnit: true })}${multiplierPostFix}`
      : noValueSign
  }

  const getValueTableCell = (
    versionId: string,
    groupedCostName: string,
    row: CostRow,
  ) => {
    const cellSum = getCellSum(versionId, groupedCostName, row)
    const isCellEditable =
      cellSum !== NO_MATCHING_RESOURCE_SYMBOL &&
      handleCostClick &&
      isFixedCostRow(row)

    return isCellEditable ? (
      <TableCell
        className={classes.editableCell}
        key={`${type}-${getCostRowTitle(row)}-${versionId}`}
        onClick={() => handleCostClick(versionId, row, groupedCostName)}
        align="center"
      >
        {cellSum}
      </TableCell>
    ) : (
      <TableCell
        align="center"
        key={`${type}-${getCostRowTitle(row)}-${versionId}`}
      >
        {cellSum}
      </TableCell>
    )
  }

  return (
    <TableRow
      key={`${type}-${getCostRowTitle(costRow)}-${
        isFixedCostRow(costRow) ? costRow.description : ''
      }`}
    >
      <TableCell className={classes.root}>{getRowText(costRow)}</TableCell>
      {versions.map((version) => getValueTableCell(version.id, title, costRow))}
    </TableRow>
  )
}
